/**
 * API service for handling HTTP requests to the backend.
 * Provides methods for GET, POST requests and error handling.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Interface representing a document resource from the API.
 */
export interface Document {
  /** Unique identifier for the document */
  id: string;
  /** Display name of the document */
  name: string;
  /** Document description or summary */
  description: string;
  /** Array of document attributes with descriptions and values */
  attributes: Array<{ description: string; value: string }>;
}

/**
 * Interface representing a product resource from the API.
 */
export interface Product {
  /** Unique product identifier */
  product_id: string;
  /** Product display name */
  name: string;
  /** Array of product attributes with descriptions and values */
  attributes: Array<{ description: string; value: string }>;
  /** Additional dynamic properties */
  [key: string]: any;
}

/**
 * Interface representing a collection of products organized by type.
 */
export interface ProductSet {
  /** Container for different product categories */
  articles: {
    /** Array of base products */
    base: Array<Product>;
  };
}

/**
 * Service for handling HTTP API requests to the backend.
 *
 * @remarks Provides centralized HTTP communication with the backend API.
 * Handles authentication headers, error formatting, and common request patterns.
 * Supports both JSON and binary (Blob) responses.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  /** @private API base URL from environment configuration */
  private apiUrl = environment.api.url;
  /** @private API name for authentication header */
  private apiName = environment.api.name;
  /** @private API key for authentication header */
  private apiKey = environment.api.key;

  /**
   * Creates an instance of ApiService.
   *
   * @param http - Angular HTTP client for making requests
   */
  constructor(private http: HttpClient) {}

  /**
   * Sends a GET request and returns an observable
   *
   * @template T - The expected response type
   * @param path - Request path to append to base URL
   * @param params - HTTP parameters which should be sent along with the request
   * @param options - Optional request options
   * @returns An observable containing the response data
   */
  get<T>(path: string = '', params?: HttpParams, options: Record<string, any> = {}): Observable<T> {
    const requestOptions = {
      headers: this.getHeaders(),
      params: params || new HttpParams(),
      ...options,
    };

    return this.http.get<T>(`${this.apiUrl}${path}`, requestOptions).pipe(catchError(error => this.formatErrors(error)));
  }

  /**
   * Gets a blob resource from the API.
   *
   * @remarks Fetches binary data (files, images, etc.) as a Blob.
   * Useful for downloading files or handling binary content.
   *
   * @param path - Request path to append to base URL
   * @param params - HTTP parameters for the request
   * @returns Observable of Blob containing the binary data
   */
  getBlob(path: string, params: HttpParams): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}${path}`, {
        params,
        responseType: 'blob',
        headers: this.getHeaders(),
      })
      .pipe(catchError(error => this.formatErrors(error)));
  }

  /**
   * Sends a POST request and returns an observable
   *
   * @template T - The expected response type
   * @param path - Request path to append to base URL
   * @param body - Data to be sent with the request
   * @param options - Optional request options
   * @returns An observable containing the response data
   */
  post<T>(path: string = '', body: object = {}, options: Record<string, any> = {}): Observable<T> {
    const requestOptions = {
      headers: this.getHeaders(),
      ...options,
    };

    return this.http.post<T>(`${this.apiUrl}${path}`, body, requestOptions).pipe(catchError(error => this.formatErrors(error)));
  }

  /**
   * Returns the HTTP headers for API requests.
   *
   * @remarks Creates authentication headers required by the API.
   * Includes API name and API key for request authorization.
   *
   * @returns HttpHeaders object with the necessary authentication headers
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      api: this.apiName,
      'api-key': this.apiKey,
    });
  }

  /**
   * Formats and handles API errors.
   *
   * @remarks Processes HTTP errors and creates user-friendly error messages.
   * Distinguishes between client-side and server-side errors.
   * Logs errors to console for debugging purposes.
   *
   * @param error - The HTTP error response to format
   * @returns An observable that will throw the formatted error
   */
  private formatErrors(error: HttpErrorResponse): Observable<never> {
    console.error('API Error:', error);

    let errorMessage = 'An error occurred with the API request';
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}, Message: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }
}
